<template>
	<!-- Positioned parent for the scroll-to-latest control, which floats over the transcript. -->
	<div class="help-message-list">
		<!--
			Deliberately NOT smooth-scrolling. The autoscroll assigns scrollTop on every token, and
			under smooth behaviour each assignment starts an animation that interrupts the one still
			running, so the view lurches and never settles. Instant scrolling moves it by the height
			of one token, which reads as the text growing in place. Smoothness belongs only to the
			scroll-to-latest control, which asks for it per call (AB#27211).
		-->
		<div
			ref="listEl"
			data-testid="help-assistant-message-list"
			class="help-message-list__scroller"
			@scroll="handleScroll"
		>
			<div
				v-if="isEmpty"
				class="help-message-list__empty"
			>
				<p class="help-message-list__greeting">
					{{ greeting }}
				</p>
				<p
					data-testid="help-assistant-disclaimer"
					class="help-message-list__disclaimer"
				>
					Answers are AI-generated from Syrenis documentation and may contain mistakes. Verify
					important details before acting on them.
				</p>

				<!-- Stacked, as in the React panel: the sheet is narrow and one per line reads better. -->
				<help-suggestion-chips
					:questions="suggestedQuestions"
					layout="column"
					test-id-prefix="help-assistant-suggestion"
					@select="$emit('suggest', $event)"
				/>
			</div>

			<template v-else>
				<div
					v-for="(message, index) in messages"
					:key="message.uid"
					:class="[
						'help-message-list__turn',
						message.role === 'user' ? 'help-message-list__turn--user' : 'help-message-list__turn--assistant'
					]"
				>
					<!--
						In-flight turn. Mirrors the React HelpAssistant, which renders a Marker with a spinner
						and shimmering copy here (AB#27211). A bare row, NOT a bubble: the shimmer sweeps the
						text itself, and a bubble's fill behind it turns that into a shimmering block, which is
						not what React shows. The spinner is decoration; the copy is the announcement, carried
						by the list's own aria-live region. Keep the two implementations in step.
					-->
					<span
						v-if="isTyping(message)"
						data-testid="help-assistant-typing"
						class="help-message-list__typing"
					>
						<LucideIcon
							name="Loader2"
							:size="16"
							aria-hidden="true"
							class="help-message-list__typing-spinner"
						/>
						<!--
							A running tool says something specific, so it replaces the generic line rather than
							shimmering alongside the hint below it.
						-->
						<span class="shimmer help-message-list__typing-text">
							{{ toolActivity || 'Generating response…' }}
						</span>
					</span>

					<div
						v-else
						:class="[
							'help-message-list__bubble',
							message.role === 'user' ? 'help-message-list__bubble--user' : 'help-message-list__bubble--assistant'
						]"
					>
						<!-- renderBody uses markdown-it's html:false default (raw HTML in the source is
						escaped, never passed through) as the first line of defense; v-dompurify-html
						sanitises the rendered markdown output as the second (AB#26561 PR #2723 review). -->
						<div
							v-if="message.role === 'assistant' && !message.isError"
							v-dompurify-html="renderBody(message.content)"
							class="help-message-list__markdown"
						/>
						<template v-else>
							{{ message.content }}
						</template>
					</div>

					<span
						v-if="message.role === 'user' && message.timestamp"
						:data-testid="`help-assistant-timestamp-${index}`"
						class="help-message-list__timestamp"
					>
						{{ formatMessageTimestamp(message.timestamp) }}
					</span>

					<!--
						Sits in the same row the action row uses on a successful turn, so the column has one
						left edge, and carries the circular arrow regenerate uses: the two never appear
						together, and one glyph for "do that again" is what every major assistant shows
						(AB#27211).
					-->
					<div
						v-if="canRetry(message, index)"
						class="help-message-list__actions"
					>
						<button
							type="button"
							:data-testid="`help-assistant-retry-${index}`"
							:disabled="streaming"
							class="help-message-list__icon-button help-message-list__retry"
							@click="$emit('retry', { index })"
						>
							<LucideIcon
								name="RefreshCw"
								:size="14"
							/>
							Retry
						</button>
					</div>

					<help-message-actions
						v-if="showActionsRow(message)"
						:index="index"
						:rating="ratings[message.id] || null"
						:rate-disabled="Boolean(ratings[message.id]) || message.id === undefined"
						:copied="copiedUid === message.uid"
						:is-last="index === messages.length - 1"
						:timestamp="message.timestamp || ''"
						@copy="handleCopy(index, message.uid, message.content)"
						@rate="handleRate(message.id, $event)"
						@regenerate="$emit('regenerate')"
					/>

					<help-suggestion-chips
						v-if="showFollowups(message, index)"
						:questions="messageFollowups(message.content)"
						layout="column"
						test-id-prefix="help-assistant-followup"
						@select="$emit('followup', $event)"
					/>
				</div>
			</template>

			<!--
				Mirrors the React HelpAssistant, which renders this hint as a Marker with a spinner and
				shimmering copy (AB#27211). Carries no role/aria-live of its own: the single live region
				below owns every announcement for this transcript.
			-->
			<div
				v-if="streaming && toolActivity && !awaitingFirstToken"
				data-testid="help-assistant-tool-activity"
				class="help-message-list__tool-activity"
			>
				<LucideIcon
					name="Loader2"
					:size="14"
					aria-hidden="true"
					class="help-message-list__tool-activity-spinner"
				/>
				<span class="shimmer">
					{{ toolActivity }}
				</span>
			</div>

			<!-- The one live region for this transcript (AB#26561 PR #2723 review) - nested live
			regions (this used to also live on the scrolling container and on the tool-activity hint
			above) have undefined behaviour across screen readers, so there is exactly one, and the
			tool-activity text is folded into it rather than announced separately. -->
			<div
				role="status"
				data-testid="help-assistant-live-status"
				class="sr-only"
			>
				{{ liveStatus }}
			</div>
		</div>

		<button
			v-if="!stickToBottom"
			type="button"
			data-testid="help-assistant-scroll-to-latest"
			aria-label="Scroll to latest"
			class="help-message-list__scroll-latest"
			@click="scrollToLatest"
		>
			<LucideIcon
				name="ArrowDown"
				:size="16"
			/>
		</button>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import HelpSuggestionChips from './help-suggestion-chips.vue'
import HelpMessageActions from './help-message-actions.vue'
import { splitFollowups, formatMessageTimestamp } from './utils/chat-core.js'
import { renderMarkdown } from './utils/markdown.js'
import { shouldStickToBottom } from './utils/message-list-core.js'
import { HELP_ASSISTANT_GREETING } from './utils/assistant-copy.js'

/**
 * Presentational chat transcript for the help assistant (AB#26561).
 *
 * Props: `messages` ({role, content, isError, id, uid, timestamp}[]), `streaming` (Boolean),
 * `toolActivity` (String|null), `ratings` (Object, keyed by server-assigned messageId).
 *
 * Emits:
 * - `retry` `{ index }` - the message index of the error turn whose Retry was clicked. Any
 *   error turn is retryable (not just the last one), so the payload is required: more than one
 *   error bubble can render a Retry link at once, and the panel needs to know which fired.
 * - `rate` `{ messageId, rating }`
 * - `copy` `{ index }`
 * - `regenerate` (no payload - always applies to the last assistant message)
 * - `followup` `{ text }`
 * - `suggest` `{ text }` (a starter question from the empty state; same payload as `followup`)
 */
export default {
	name: 'help-message-list',
	components: {
		LucideIcon,
		HelpSuggestionChips,
		HelpMessageActions
	},
	props: {
		messages: {
			type: Array,
			default: () => []
		},
		streaming: {
			type: Boolean,
			default: false
		},
		toolActivity: {
			type: String,
			default: null
		},
		ratings: {
			type: Object,
			default: () => ({})
		},
		// Mirrors the React HelpAssistant's `greeting` prop: what the assistant says before a
		// single question is asked, which has to agree with the questions offered beneath it.
		greeting: {
			type: String,
			default: HELP_ASSISTANT_GREETING
		},
		/** Starter questions for the empty state, the Vue twin of the React panel's
		 * `suggestedQuestions`. Three is what fits. */
		suggestedQuestions: {
			type: Array,
			default: () => []
		}
	},
	data () {
		return {
			copiedUid: null,
			copiedTimeout: null,
			stickToBottom: true,
			// The transcript's one live region (AB#26561 PR #2723 review) - see the watchers
			// below for when this changes.
			liveStatus: ''
		}
	},
	computed: {
		/** True while the assistant's placeholder is still empty. The typing row owns the status line
		 * until the first token lands, so the hint below must not render a second one (AB#27211). */
		awaitingFirstToken () {
			const tail = this.messages[this.messages.length - 1]
			return Boolean(this.streaming && tail && tail.role === 'assistant' && !tail.isError && tail.content === '')
		},
		isEmpty () {
			return this.messages.length === 0
		}
	},
	watch: {
		messages () {
			this.scrollIfStuck()
		},
		streaming (isStreaming, wasStreaming) {
			if (isStreaming) {
				// A turn starting is the reader's own doing (send, retry, regenerate), and whoever
				// just asked wants to see the answer. Re-attach the autoscroll even if they had
				// scrolled up to reread something: left detached, the reply lands below the fold
				// and only the scroll-to-latest control hints that it arrived. Mirrors the React
				// HelpAssistant, whose runTurn resets stickToBottomRef the moment a turn begins.
				this.stickToBottom = true
				this.scrollToBottom()
				this.liveStatus = this.toolActivity || 'Assistant is responding'
			} else if (wasStreaming) {
				this.liveStatus = 'Response complete'
			}
		},
		toolActivity (activity) {
			this.scrollIfStuck()
			if (this.streaming) this.liveStatus = activity || 'Assistant is responding'
		}
	},
	mounted () {
		this.scrollToBottom()
	},
	beforeDestroy () {
		if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
	},
	methods: {
		formatMessageTimestamp,
		messageFollowups (content) {
			return splitFollowups(content).followups
		},
		renderBody (content) {
			return renderMarkdown(splitFollowups(content).body)
		},
		isTyping (message) {
			return message.role === 'assistant' && !message.isError && message.content === '' && this.streaming
		},
		canRetry (message, index) {
			const previous = this.messages[index - 1]
			return Boolean(message.isError) && Boolean(previous) && previous.role === 'user'
		},
		showActionsRow (message) {
			return message.role === 'assistant' && !message.isError && !this.streaming && message.content !== ''
		},
		showFollowups (message, index) {
			return message.role === 'assistant' &&
				!message.isError &&
				!this.streaming &&
				index === this.messages.length - 1 &&
				this.messageFollowups(message.content).length > 0
		},
		handleRate (messageId, rating) {
			if (messageId === undefined || this.ratings[messageId]) return
			this.$emit('rate', { messageId, rating })
		},
		async handleCopy (index, uid, content) {
			const text = splitFollowups(content).body
			try {
				await navigator.clipboard.writeText(text)
			} catch {
				return
			}
			this.copiedUid = uid
			if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
			this.copiedTimeout = setTimeout(() => {
				this.copiedUid = null
			}, 2000)
			this.$emit('copy', { index })
		},
		handleScroll () {
			const el = this.$refs.listEl
			if (!el) return
			this.stickToBottom = shouldStickToBottom({
				scrollHeight: el.scrollHeight,
				scrollTop: el.scrollTop,
				clientHeight: el.clientHeight
			})
		},
		scrollIfStuck () {
			if (this.stickToBottom) this.scrollToBottom()
		},
		scrollToBottom () {
			this.$nextTick(() => {
				const el = this.$refs.listEl
				if (el) el.scrollTop = el.scrollHeight
			})
		},
		/** Returns the reader to the newest message and re-attaches the autoscroll. */
		scrollToLatest () {
			const el = this.$refs.listEl
			if (!el) return
			el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
			this.stickToBottom = true
		}
	}
}
</script>

<style lang="scss" scoped>
.help-message-list {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;

  &__scroller {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
    overflow-y: auto;

  }

  /* Scroll to latest, the one affordance the transcript was missing: scrolling up mid-answer left
     no way back and no sign that more had arrived. Circular on purpose, the one place in the panel
     that is: it floats over the transcript rather than being a surface or a field, and every
     assistant this is modelled on draws it the same way (AB#27211). */
  &__scroll-latest {
    position: absolute;
    bottom: 12px;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: var(--foreground, #4A5568);
    cursor: pointer;
    background-color: var(--card, #FFFFFF);
    border: 1px solid var(--border, #E0E2E5);
    border-radius: var(--radius-full, 9999px);
    box-shadow: 0 1px 3px rgb(0 0 0 / 10%);
    transform: translateX(-50%);

    &:hover {
      background-color: var(--muted, #F5F5F5);
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    &:focus-visible {
      outline: 2px solid var(--primary, #406A94);
      outline-offset: 2px;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__greeting {
    margin: 0;
    padding: 8px 12px;
    font-size: 0.875rem;
    color: var(--foreground, #4A5568);
    background-color: var(--muted, #ECECED);
    border-radius: var(--radius, 8px);
  }

  &__disclaimer {
    margin: 0;
    padding: 0 4px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);
  }

  &__turn {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &--user {
      align-items: flex-end;
    }

    &--assistant {
      align-items: flex-start;
    }
  }

  &__bubble {
    max-width: 85%;
    padding: 8px 12px;
    font-size: 0.875rem;
    line-height: 1.5;
    word-break: break-word;
    border-radius: var(--radius, 8px);

    &--user {
      color: var(--primary-foreground, #FFFFFF);
      background-color: var(--primary, #5C7B9D);
    }

    &--assistant {
      color: var(--foreground, #4A5568);
      background-color: var(--muted, #ECECED);
    }
  }

  &__markdown {
    ::v-deep p {
      margin: 0.5em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    ::v-deep ul,
    ::v-deep ol {
      margin: 0.5em 0;
      padding-left: 1.25em;
    }

    ::v-deep pre {
      padding: 8px;
      overflow-x: auto;
      font-size: 0.8125rem;
      background-color: var(--background, #F5F5F5);
      border: 1px solid var(--border, #E0E2E5);
      border-radius: var(--radius-sm, 4px);
    }

    ::v-deep code {
      font-size: 0.8125rem;
    }
  }

  &__typing {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    padding: 4px 0;
    font-size: 0.875rem;
    color: var(--muted-foreground, #666E7A);
  }

  &__typing-spinner {
    flex: none;
    animation: help-message-list-spin 1s linear infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  // .shimmer itself lives in shared/styles/shared-styles.css, beside its React twin.
  &__typing-text {
    min-width: 0;
  }

  &__timestamp {
    padding: 0 4px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);
  }

  // A muted control rather than an underlined link, matching the icon buttons beside it.
  &__retry {
    gap: 6px;
    padding: 6px;
    font-family: inherit;
    font-size: 0.8125rem;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  // 4px matches the React action row, so retry and the message actions share one left edge.
  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 0 4px;
  }

  // Matches the header icon buttons on both platforms (HelpPanelTrigger, ProductSwitcher): the
  // house treatment is a 6px radius, a half-strength muted hover and a primary focus ring, not the
  // 4px chip radius and full-strength hover this row used to carry (AB#27211).
  &__icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    min-height: 24px;
    padding: 6px;
    color: var(--muted-foreground, #666E7A);
    cursor: pointer;
    background: none;
    border: none;
    border-radius: calc(var(--radius-md, 0.5rem) - 2px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      color: var(--foreground, #4A5568);
      background-color: var(--muted, #F5F5F5);
      // Half-strength, so the hover reads as a lift rather than a filled chip. Declared after the
      // solid fallback so a browser without color-mix keeps the older treatment.
      background-color: color-mix(in srgb, var(--muted, #F5F5F5) 50%, transparent);
    }

    &:focus-visible {
      outline: 2px solid var(--primary, #406A94);
      outline-offset: 0;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

  }

  &__tool-activity {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 0 4px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #666E7A);

    // .shimmer itself lives in shared/styles/shared-styles.css, beside its React twin.
    &-spinner {
      flex: none;
      animation: help-message-list-spin 1s linear infinite;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
  }
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes help-message-list-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
